<!--
  VisibilityButton.vue

  Pill picker for the visibility FieldType. Clicking a pill commits
  immediately — no overlay edit mode. Used directly as the `form` slot
  and wrapped by VisibilityCell for the `cell` slot.
-->
<template>
  <div class="visibility-button">
    <div class="visibility-pills">
      <button
        v-for="option in visibilities"
        :key="option.value"
        type="button"
        class="visibility-pill"
        :class="pillClass(option.value)"
        :title="option.label"
        :disabled="readonly"
        @click="handleSelect(option.value)"
      >
        <font-awesome-icon :icon="option.icon" size="sm" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Field } from "@auger/core/fields"
import { visibilities } from "../visibilities"

// `field` is accepted for protocol uniformity (RecordCell passes it to
// every dispatched cell) but not read — visibility's options are baked
// into the type definition, not the field instance.
const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    field?: Field
    readonly?: boolean
  }>(),
  {
    modelValue: null,
    field: undefined,
    readonly: false,
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: string]
}>()

function pillClass(value: string) {
  return {
    active: props.modelValue === value,
    [value]: true,
    "br-0": value === "private",
    "rtl-3": value === "private",
    "rbl-3": value === "private",
    "bl-0": value === "public",
    "rtr-3": value === "public",
    "rbr-3": value === "public",
  }
}

function handleSelect(value: string) {
  if (props.readonly) return
  emit("update:modelValue", value)
}
</script>

<style>
.visibility-button {
  .visibility-pills {
    display: inline-flex;
    border-radius: 4px;
    overflow: hidden;
  }

  .visibility-pill {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 22px;
    padding: 0;
    border: 1px solid var(--auger-border);
    background: var(--auger-bg);
    color: var(--auger-text-weak);
    cursor: pointer;
    transition: all 0.15s ease;

    &:not(:last-child) {
      border-right: 1px solid var(--auger-border);
    }

    &:hover:not(.active) {
      background: var(--auger-bg);
    }

    &.active {
      color: white;

      &.private {
        background-color: rgb(146, 171, 197);
        border-color: rgb(146, 171, 197);
      }

      &.shared {
        background-color: slategray;
        border-color: slategray;
      }

      &.public {
        background-color: rgb(74, 85, 96);
        border-color: rgb(74, 85, 96);
      }
    }

    &:disabled {
      cursor: default;
    }
  }
}
</style>
